import { FormProps } from '@alphabait/ui/form'
import { useRef, useState } from 'react'
import { FileUploadInput } from './custom-types'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import { tokenState } from '../../store/atoms/token'
import { displayS3File } from '../../utils/s3'
import { CloudArrowUp, MicrosoftExcelLogo } from '@phosphor-icons/react'
import { uploadFileGeneric } from '../../utils/file-upload-helper'
import { useController, Control } from 'react-hook-form'
import cx from 'classnames'

// Legacy mode props (used with AlphaForm)
type LegacyProps = {
  field: FileUploadInput
  formProps: FormProps
}

// Direct mode props (used with plain react-hook-form)
type DirectProps = {
  name: string
  control: Control<any>
  uploadUrl: string
  allowedFileTypes?: string[]
  uploadBody?: Record<string, unknown>
  disabled?: boolean
  testId?: string
  className?: string
  error?: { message?: string }
  hint?: string
  // The endpoint's answer -> the value to store. Default: the answer as is.
  parseResponse?: (response: string) => string
  // When set, a "remove" link clears an uploaded file.
  onClear?: () => void
}

// Discriminated union - component accepts either legacy or direct props
type CustomFileUploadProps = LegacyProps | DirectProps

function isLegacyProps(props: CustomFileUploadProps): props is LegacyProps {
  return 'field' in props && 'formProps' in props
}

export default function CustomFileUpload(props: CustomFileUploadProps) {
  // Extract the required values from either legacy or direct props
  const fieldName = isLegacyProps(props) ? props.field.name : props.name
  const control = isLegacyProps(props) ? props.formProps.control : props.control
  const uploadUrl = isLegacyProps(props) ? props.field.uploadUrl : props.uploadUrl
  const allowedFileTypes = isLegacyProps(props)
    ? props.field.allowedFileTypes
    : props.allowedFileTypes
  const uploadBody = isLegacyProps(props) ? props.field.uploadBody : props.uploadBody
  const disabled = isLegacyProps(props)
    ? props.field.uiOptions?.disabled
    : props.disabled
  const testId = isLegacyProps(props)
    ? props.field.uiOptions?.testId
    : props.testId
  const externalError = isLegacyProps(props) ? undefined : props.error
  const hint = isLegacyProps(props) ? undefined : props.hint
  const parseResponse = isLegacyProps(props) ? undefined : props.parseResponse
  const onClear = isLegacyProps(props) ? undefined : props.onClear
  const formValues = isLegacyProps(props) ? props.formProps.formValues : undefined
  const register = isLegacyProps(props) ? props.formProps.register : undefined

  const { t } = useTranslation()
  const token = useRecoilValue(tokenState)
  const [_file, setFile] = useState<File>()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isImgFileType = allowedFileTypes?.find(
    (fileType) => fileType === 'jpg' || fileType === 'png',
  )
  const xlsFileTypes = ['csv', 'xls', 'xlsx']
  const isSpreadsheet = allowedFileTypes?.find((fileType) =>
    xlsFileTypes.includes(fileType),
  )

  // For legacy mode, extract register props; for direct mode, register is undefined
  const registerProps = register ? register(fieldName) : { ref: undefined }
  const { ref: _ref, ...rest } = registerProps

  const getValue = (obj: Record<string, unknown>, path: string): unknown => {
    return path.split('.').reduce((o: unknown, p: string) => {
      if (o && typeof o === 'object' && p in o) {
        return (o as Record<string, unknown>)[p]
      }
      return undefined
    }, obj)
  }

  const {
    field: { onChange, value: v },
    fieldState: { error: fieldError },
  } = useController({
    control,
    name: fieldName,
  })

  // Use external error if provided, otherwise use field error
  const error = externalError || fieldError

  const value = formValues ? v ?? getValue(formValues, fieldName) : v

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  const handleFileUpload = async (file?: File) => {
    if (!file) {
      return
    }

    const fileReader = new FileReader()

    fileReader.readAsDataURL(file)

    const formData = new FormData()
    formData.append('file', file as Blob)

    if (uploadBody) {
      Object.entries(uploadBody).forEach(([key, value]) => {
        if (value) {
          if (typeof value === 'object' && value !== null) {
            formData.append(key, JSON.stringify(value))
          } else {
            formData.append(key, value.toString())
          }
        }
      })
    }

    try {
      const fileName = await uploadFileGeneric({
        file,
        uploadUrl,
        uploadBody,
        token: token?.__raw,
      })
      onChange(parseResponse ? parseResponse(fileName) : fileName)
    } catch (error) {
      console.error(error)
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const files = event.dataTransfer.files
    if (files.length !== 1) {
      // TODO: Add a visual error message in the area
      console.log('Please drop one file at a time')
      return
    }
    handleFileUpload(files[0])
    setFile(files[0])
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files?.length === 1) {
      handleFileUpload(files[0])
      setFile(files[0])
    }
  }

  const openFilePicker = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    fileInputRef.current?.click()
  }

  const allowedFiles =
    allowedFileTypes
      ?.map((fileType) => fileType.toUpperCase())
      ?.map((fileType) => (fileType === 'XLSX' ? 'Excel' : fileType)) || []

  return (
    <div>
      <div
        className={cx(
          disabled ? 'pointer-events-none' : 'cursor-pointer',
          'flex flex-col items-center space-y-1 border-2 border-dashed border-gray-500 rounded-md pt-6 pb-7',
        )}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={openFilePicker}
      >
        <div className="flex flex-row space-between w-full items-center">
          <div className="flex-1 mx-4">
            {isImgFileType && value && (
              <img
                className="rounded-md max-h-40"
                src={displayS3File(value?.toString())}
              />
            )}
            {isSpreadsheet && value && (
              <MicrosoftExcelLogo size={32} className="self-center" />
            )}
          </div>
          <div className="flex-1 flex flex-col items-center">
            <CloudArrowUp size={48} />
            <p className="text-center">
              <span className="text-secondary">
                {t('file_upload.upload_file')}
              </span>
              <span> {t('file_upload.drag_and_drop')}</span>
              <span className="block text-gray-400">
                {t('file_upload.a')}{' '}
                {allowedFiles.length === 1
                  ? allowedFiles.join(' ')
                  : allowedFiles.slice(0, -1).join(' ') +
                  ' ' +
                  t('file_upload.or') +
                  ' ' +
                  allowedFiles.slice(-1)}{' '}
                {t('file_upload.file')}
              </span>
            </p>
            <input
              disabled={disabled}
              data-testid={testId}
              type="file"
              className="hidden"
              {...rest}
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept={allowedFileTypes
                ?.map((fileTypeExt) => `.${fileTypeExt}`)
                .join(',')}
            />
          </div>
        </div>
      </div>
      {hint && (
        <p className="text-gray-400 text-xs mt-1">{hint}</p>
      )}
      {onClear && value && (
        <button
          type="button"
          className="text-xs text-destructive mt-1"
          onClick={onClear}
        >
          {t('file_upload.remove', { defaultValue: 'Remove' })}
        </button>
      )}
      {error && (
        <p className="text-primary text-sm mt-1">
          {t(`validation_messages.${error.message}`, { defaultValue: error.message })}
        </p>
      )}
    </div>
  )
}
